import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'

import { ReportMaintenanceApi } from '../api/ReportMaintenanceApi'

import { useRbac, useWorkspace } from '@/features/account'
import { useI18n } from '@/shared/i18n'
import { useToast } from '@/shared/ui'

export type ReportMaintenanceAction = 'clear' | 'execute'

/**
 * Sysadmin-only report maintenance command owner. Both the legacy button adapter and the shared
 * report overflow consume this seam so RBAC, payload shape, busy state and feedback cannot drift.
 */
export function useReportMaintenance(
  scheduleIdSource: MaybeRefOrGetter<number | null | undefined>,
) {
  const rbac = useRbac()
  const workspace = useWorkspace()
  const toast = useToast()
  const { t } = useI18n()
  const api = new ReportMaintenanceApi()
  const busy = ref<ReportMaintenanceAction | null>(null)
  const available = computed(() => rbac.isSysAdmin.value && toValue(scheduleIdSource) != null)

  async function run(action: ReportMaintenanceAction): Promise<void> {
    const scheduleId = toValue(scheduleIdSource)
    const workspaceId = workspace.workspaceId.value
    if (!available.value || busy.value || scheduleId == null || workspaceId === undefined) return
    busy.value = action
    try {
      const payload = { schedule_id: scheduleId, workspace_id: Number(workspaceId) }
      if (action === 'clear') {
        await api.clearCache(payload)
        toast.success(t('reports.maintenance.cacheCleared'))
      } else {
        await api.executeAnalysis(payload)
        toast.success(t('reports.maintenance.analysisExecuted'))
      }
    } catch {
      toast.error(t('reports.maintenance.failed'))
    } finally {
      busy.value = null
    }
  }

  return { available, busy, run }
}
